import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ShoppingBagIcon, Trash2Icon, XIcon } from 'lucide-react';
import { useCart } from '../../contexts/CartContext';
import { formatPrice } from '../../utils/currency';
import { FREE_SHIPPING_THRESHOLD } from '../../utils/shipping';
import { QuantityStepper } from '../shop/QuantityStepper';
const ease = [0.23, 1, 0.32, 1] as const;
export function CartDrawer() {
  const {
    isOpen,
    close,
    lines,
    subtotal,
    setQty,
    remove,
    count
  } = useCart();
  const navigate = useNavigate();
  const remaining = Math.max(0, FREE_SHIPPING_THRESHOLD - subtotal);
  const progress = Math.min(1, subtotal / FREE_SHIPPING_THRESHOLD);
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, close]);
  return <AnimatePresence data-id="element-141" data-mp="ci5rsz12ajv2">
      {isOpen && <div className="fixed inset-0 z-50" data-id="element-142" data-mp="cj72h2e614c9">
          <motion.div className="absolute inset-0 bg-ink/40" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={close} data-id="element-143" data-mp="hhib59sq58sw" />
          <motion.aside role="dialog" aria-modal="true" aria-label="Winkelwagen" className="absolute inset-y-0 right-0 flex w-full max-w-md flex-col bg-canvas" initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        duration: 0.28,
        ease
      }} data-id="element-144" data-mp="7hrkjj7abdy3">
            <div className="flex h-16 items-center justify-between border-b border-line px-6" data-id="element-145" data-mp="3kz8xteo6jv75">
              <h2 className="text-lg font-semibold tracking-tight" data-id="element-146" data-mp="36zr6osnew5um">Winkelwagen {count > 0 && <span className="text-muted" data-id="element-147" data-mp="3byapwlamf3ja">({count})</span>}</h2>
              <button onClick={close} className="grid h-10 w-10 place-items-center rounded-full hover:bg-surface" aria-label="Sluiten" data-id="element-148" data-mp="3bzlvmqnmmld1">
                <XIcon className="h-5 w-5" data-id="element-149" data-mp="1cqlxrlpgoy2q" />
              </button>
            </div>

            {lines.length === 0 ? <div className="flex flex-1 flex-col items-center justify-center px-8 text-center" data-id="element-150" data-mp="17skht9nc1evh">
                <span className="grid h-14 w-14 place-items-center rounded-full bg-surface" data-id="element-151" data-mp="13rgygd62t0pi">
                  <ShoppingBagIcon className="h-6 w-6 text-muted" data-id="element-152" data-mp="20f56g2cfbcvc" />
                </span>
                <p className="mt-5 text-lg font-semibold" data-id="element-153" data-mp="1vierxjg9opc1">Je winkelwagen is leeg</p>
                <p className="mt-1 text-sm text-muted" data-id="element-154" data-mp="1rf8m2cx2h063">Begin met onze bestseller: het complete hammam-ritueel.</p>
                <Link to="/product/hammam-ritueel-set" onClick={close} className="mt-6 inline-flex h-12 items-center rounded-full bg-ink px-6 text-sm font-medium text-canvas hover:bg-berry" data-id="element-155" data-mp="1mflz3brfs5k3">
                  Bekijk de Hammam Ritueel Set
                </Link>
              </div> : <>
                <div className="border-b border-line px-6 py-4" data-id="element-156" data-mp="2rn5cfpjah22w">
                  <p className="text-sm" data-id="element-157" data-mp="2rlkfjt9i571v">
                    {remaining > 0 ? <>Nog <strong data-id="element-158" data-mp="2vnfkd7qn62ke">{formatPrice(remaining)}</strong> tot gratis verzending</> : <strong className="text-olive" data-id="element-159" data-mp="35fbtrey6hf3x">Je bestelling wordt gratis verzonden</strong>}
                  </p>
                  <div className="mt-2.5 h-1.5 overflow-hidden rounded-full bg-line" data-id="element-160" data-mp="3addcfyrozcev">
                    <div className={`h-full rounded-full transition-[width] duration-300 ease-out ${remaining > 0 ? 'bg-gradient-to-r from-pink to-[#F59AC0]' : 'bg-olive'}`} style={{
                width: `${progress * 100}%`
              }} data-id="element-161" data-mp="3fd9evtkqqvsb" />
                  </div>
                </div>

                <ul className="flex-1 divide-y divide-line overflow-y-auto px-6" data-id="element-162" data-mp="1p1v551uh9flq">
                  {lines.map(l => <li key={`${l.slug}-${l.variantId}`} className="flex gap-4 py-5" data-id="element-163" data-mp="tszxtgkmfrjx">
                      <Link to={`/product/${l.slug}`} onClick={close} className="shrink-0" data-id="element-164" data-mp="pp1x51fne8or">
                        <img src={l.product.image} alt="" className="h-20 w-20 rounded-xl bg-surface object-cover" data-id="element-165" data-mp="13oktyy30dxlr" />
                      </Link>
                      <div className="flex flex-1 flex-col" data-id="element-166" data-mp="b05rtc4z79ru">
                        <div className="flex justify-between gap-3" data-id="element-167" data-mp="b0ozm00wwhs5">
                          <div data-id="element-168" data-mp="62dn1hj8ft68">
                            <p className="font-medium" data-id="element-169" data-mp="635i8r8x3ovz">{l.product.name}</p>
                            <p className="text-sm text-muted" data-id="element-170" data-mp="kr0daeaee5c8">{l.variant.label}</p>
                          </div>
                          <p className="font-medium tabular-nums" data-id="element-171" data-mp="fxxftv2i2bqr">{formatPrice(l.total)}</p>
                        </div>
                        <div className="mt-auto flex items-center justify-between pt-3" data-id="element-172" data-mp="3ocz335qpi1kp">
                          <QuantityStepper value={l.qty} onChange={q => setQty(l.slug, l.variantId, q)} data-id="element-173" data-mp="3je4n0bk4ir4a" />
                          <button onClick={() => remove(l.slug, l.variantId)} className="grid h-9 w-9 place-items-center rounded-full text-muted hover:bg-surface hover:text-ink" aria-label={`${l.product.name} verwijderen`} data-id="element-174" data-mp="1ywda32ex210s">
                            <Trash2Icon className="h-4 w-4" data-id="element-175" data-mp="2ho60qq6ija5c" />
                          </button>
                        </div>
                      </div>
                    </li>)}
                </ul>

                <div className="border-t border-line px-6 py-5" data-id="element-176" data-mp="28my8aoqoh3j6">
                  <div className="flex items-baseline justify-between" data-id="element-177" data-mp="2dmuaciftekw3">
                    <span className="text-muted" data-id="element-178" data-mp="2dp719l3jw5la">Subtotaal</span>
                    <span className="text-xl font-semibold tabular-nums" data-id="element-179" data-mp="x81fr4vabw72">{formatPrice(subtotal)}</span>
                  </div>
                  <p className="mt-1 text-xs text-muted" data-id="element-180" data-mp="125ujzsd12g4p">Inclusief btw. Verzendkosten bij het afrekenen.</p>
                  <button onClick={() => {
              close();
              navigate('/afrekenen');
            }} className="mt-5 flex h-14 w-full items-center justify-center rounded-full bg-ink text-base font-medium text-canvas transition-colors duration-150 hover:bg-berry" data-id="element-181" data-mp="33z7k0fw1nuoq">
                    Afrekenen
                  </button>
                  <p className="mt-3 text-center text-xs text-muted" data-id="element-182" data-mp="2g7sao35uod3z">iDEAL · Creditcard · Klarna · PayPal</p>
                </div>
              </>}
          </motion.aside>
        </div>}
    </AnimatePresence>;
}